import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { platformAPI } from "../services/api";
import Modal from "../components/Modal";
import SendConfirmationButton from "../components/SendConfirmationButton";
import { telLink, whatsappLink } from "../utils/contact";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const when = (value) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

/**
 * Shops that signed themselves up. They are already working in a trial; this
 * is where the operator calls the owner, then approves (which allows the trial
 * to be extended and the shop to be converted) or rejects with a message,
 * which suspends the shop and is what its owner then reads.
 */
export default function Signups() {
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await platformAPI.listSignups(tab);
      setRows(data);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load signups");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (row) => {
    setBusyId(row.id);
    setError("");
    try {
      await platformAPI.approveShop(row.id);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to approve");
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (event) => {
    event.preventDefault();
    setBusyId(rejecting.id);
    setError("");
    try {
      await platformAPI.rejectShop(rejecting.id, message);
      setRejecting(null);
      setMessage("");
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || "Failed to reject");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-1">New signups</h1>
      <p className="text-sm text-gray-500 mb-6">
        These shops are already using their trial. Approval is what lets you extend it or make the shop paid.
      </p>

      <div className="flex gap-2 mb-4">
        {TABS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium ${
              tab === key ? "bg-primary-600 text-white" : "bg-white text-gray-600 shadow-sm"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="text-red-600 mb-4">{error}</p>}

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="bg-white rounded-xl shadow px-4 py-10 text-center text-gray-400">
          {tab === "pending" ? "Nobody is waiting for review." : "Nothing here."}
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li key={row.id} className="bg-white rounded-xl shadow p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/shops/${row.id}`} className="font-semibold text-primary-700 hover:underline">
                    {row.name}
                  </Link>
                  <span className="text-gray-400 text-sm"> · /s/{row.slug}</span>
                  <p className="text-sm text-gray-700">
                    {row.owner_name} · {row.owner_email}{" "}
                    {row.email_verified_at ? (
                      <span className="text-green-700">✓ email confirmed</span>
                    ) : (
                      <span className="text-amber-700">email not confirmed</span>
                    )}
                  </p>
                  {!row.email_verified_at && <SendConfirmationButton shopId={row.id} className="mt-1" />}
                  <p className="text-sm text-gray-500">
                    {row.owner_phone} · {String(row.language || "").toUpperCase()} · {row.currency} · signed up{" "}
                    {when(row.created_at)} · {row.product_count} products
                  </p>
                  {row.review_note && (
                    <p className="text-sm text-gray-600 mt-1">Note: {row.review_note}</p>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <a
                    href={telLink(row.owner_phone)}
                    className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium"
                  >
                    📞 Call
                  </a>
                  <a
                    href={whatsappLink(
                      row.owner_phone,
                      `Hello ${row.owner_name}, this is SES Vape Shop about your new shop "${row.name}".`,
                    )}
                    target="_blank"
                    rel="noreferrer"
                    className="px-3 py-1.5 rounded-lg bg-green-100 text-green-800 text-sm font-medium"
                  >
                    💬 WhatsApp
                  </a>
                  {row.review_status !== "approved" && (
                    <button
                      onClick={() => approve(row)}
                      disabled={busyId === row.id}
                      className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                    >
                      Approve
                    </button>
                  )}
                  {row.review_status === "pending" && (
                    <button
                      onClick={() => setRejecting(row)}
                      disabled={busyId === row.id}
                      className="px-3 py-1.5 rounded-lg bg-red-50 text-red-700 text-sm font-semibold disabled:opacity-50"
                    >
                      Reject
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {rejecting && (
        <Modal title={`Reject ${rejecting.name}`} onClose={() => setRejecting(null)}>
          <form onSubmit={reject} className="space-y-3">
            <p className="text-sm text-gray-600">
              The shop is suspended, and this message is what its owner and staff read when they sign in.
            </p>
            <textarea
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              rows={4}
              required
              autoFocus
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="We could not verify this shop…"
            />
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={busyId === rejecting.id}
                className="bg-red-600 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
              >
                Reject and suspend
              </button>
              <button type="button" onClick={() => setRejecting(null)} className="text-gray-500 hover:underline">
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
